/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    Collection,
    HeadlessTreeItem,
    HeadlessTreeItemContent,
    type Key,
    SidebarEmptyState,
    SidebarItemIcon,
    SidebarSection,
    SidebarTree,
    SidebarTreeItem,
    SidebarTreeItemExpandButton,
    SidebarTreeItemText,
    LoadingIndicator,
} from '@gitbook/spine-react';

import { isDraftSiteTreeItem, resolvePageTarget, useSpaceRevisions } from '../../Components';
import {
    DraftBadge,
    SiteSectionActionsButton,
    SiteSectionGroupActionsButton,
    SiteVariantActionsButton,
} from '../../Components/Site/SiteStructure';
import {
    type AnySiteItem,
    getSiteTreeItem,
    getSiteSpacesWithCustomHomePage,
    type SiteCapability,
    type SiteSectionGroupItem,
    type SiteSectionItem,
    type SiteSpaceItem,
    type SiteStructureItem,
    type UseUpgradeSite,
} from '../../state';
import { searchSiteTreeItems } from './siteTreeSearch';

interface SiteSectionGroupEmptyStateItem {
    id: string;
    type: 'site-section-group-empty-state';
    title: string;
    icon: typeof Icon.InfoCircle;
}

type SidebarSiteTreeItem = AnySiteItem | SiteSectionGroupEmptyStateItem;

/**
 * Represent the top-level keys that can be expanded/collapsed.
 */
const expandableSiteItemsAtom = atom(new Set<Key>());

/**
 * Represent the expanded state of the sites in the sidebar.
 */
const expandedSiteItemKeysAtom = atomWithStorage<Key[]>('sidebarSitesExpanded', [], undefined, {
    getOnInit: true,
});

export const expandedSiteItemsAtom = atom(
    (get) => new Set(get(expandedSiteItemKeysAtom)),
    (get, set, update: Set<Key> | ((previous: Set<Key>) => Set<Key>)) => {
        const previous = new Set(get(expandedSiteItemKeysAtom));
        const next = typeof update === 'function' ? update(previous) : update;
        if (areSetsEqual(previous, next)) {
            return;
        }
        set(expandedSiteItemKeysAtom, Array.from(next));
    }
);

/**
 * Represent the sites that have been touched by the user.
 */
const touchedSiteKeysAtom = atomWithStorage<Key[]>('sidebarSitesTouched', [], undefined, {
    getOnInit: true,
});

const touchedSitesAtom = atom(
    (get) => new Set(get(touchedSiteKeysAtom)),
    (get, set, update: Set<Key> | ((previous: Set<Key>) => Set<Key>)) => {
        const previous = new Set(get(touchedSiteKeysAtom));
        const next = typeof update === 'function' ? update(previous) : update;
        if (areSetsEqual(previous, next)) {
            return;
        }
        set(touchedSiteKeysAtom, Array.from(next));
    }
);

/**
 * Hook to manage the expanded state of site items in the sidebar.
 * If a site is not touched yet, it's be expanded by default.
 */
export function useCollapseAllSiteItems(): {
    hasExpandedItems: boolean;
    collapseAllItems: () => void;
} {
    const expandableSiteItems = useAtomValue(expandableSiteItemsAtom);
    const [expandedItems, setExpandedItems] = useAtom(expandedSiteItemsAtom);
    const [touchedItems, setTouchedItems] = useAtom(touchedSitesAtom);

    const hasExpandedItems = React.useMemo(() => {
        // An item is effectively expanded if it's either explicitly expanded,
        // or it hasn't been touched yet (default expanded state).
        for (const key of expandableSiteItems) {
            if (expandedItems.has(key) || !touchedItems.has(key)) {
                return true;
            }
        }
        return false;
    }, [expandableSiteItems, expandedItems, touchedItems]);

    const collapseAllItems = React.useCallback(() => {
        setTouchedItems((prev) => {
            const nextTouched = new Set(prev);
            for (const key of expandableSiteItems) {
                nextTouched.add(key);
            }
            return nextTouched.size !== prev.size ? nextTouched : prev;
        });
        setExpandedItems(new Set());
    }, [expandableSiteItems, setExpandedItems, setTouchedItems]);

    return { hasExpandedItems, collapseAllItems };
}

/**
 * Hook to manage the expanded state of expandable site items in the sidebar.
 * If an expandable item is not touched yet, it will be expanded by default.
 */
function useSiteItemsExpandedState(props: {
    expandableKeys: Set<Key>;
    defaultExpandedKeys: Set<Key>;
}) {
    const { expandableKeys, defaultExpandedKeys } = props;
    const [expandedKeys, setExpandedKeys] = useAtom(expandedSiteItemsAtom);
    const [touchedKeys, setTouchedKeys] = useAtom(touchedSitesAtom);
    const onExpandedChange = React.useCallback(
        (keys: Set<Key>) => {
            const nextExpandedKeys = new Set(
                Array.from(keys).filter((key) => expandableKeys.has(key))
            );
            setTouchedKeys((prev) => {
                const newTouched = new Set(prev);
                for (const key of expandableKeys) {
                    if (!nextExpandedKeys.has(key)) {
                        newTouched.add(key);
                    }
                }
                return newTouched.size !== prev.size ? newTouched : prev;
            });
            setExpandedKeys(nextExpandedKeys);
        },
        [expandableKeys, setExpandedKeys, setTouchedKeys]
    );
    const computedExpandedKeys = React.useMemo(() => {
        const expanded = new Set(Array.from(expandedKeys).filter((key) => expandableKeys.has(key)));
        // Mark default items as expanded if they are not touched yet.
        for (const key of defaultExpandedKeys) {
            if (!expanded.has(key) && !touchedKeys.has(key)) {
                expanded.add(key);
            }
        }
        return expanded;
    }, [defaultExpandedKeys, expandableKeys, expandedKeys, touchedKeys]);

    return { expandedKeys: computedExpandedKeys, onExpandedChange };
}

export function SitesTree(props: {
    sites: api.OrganizationAllSite[];
    siteId: string;
    screen: NavigationScreenState;
    organizationId: string;
    siteActionsContext?: {
        organization: api.Organization;
        site: api.Site;
        sitePermissions: SiteCapability;
        siteStructure: api.SiteStructure;
        upgrade: UseUpgradeSite | null;
    };
    searchQuery?: string;
}) {
    return (
        <React.Suspense
            fallback={
                <div className="flex justify-center p-2">
                    <LoadingIndicator size="small" />
                </div>
            }
        >
            <SitesTreeContent {...props} />
        </React.Suspense>
    );
}

function SitesTreeContent(props: {
    sites: api.OrganizationAllSite[];
    siteId: string;
    screen: NavigationScreenState;
    organizationId: string;
    siteActionsContext?: {
        organization: api.Organization;
        site: api.Site;
        sitePermissions: SiteCapability;
        siteStructure: api.SiteStructure;
        upgrade: UseUpgradeSite | null;
    };
    searchQuery?: string;
}) {
    const { sites, siteId, screen, organizationId, siteActionsContext, searchQuery } = props;
    const spacesWithCustomHomePage = React.useMemo(
        () =>
            sites.flatMap((site) =>
                getSiteSpacesWithCustomHomePage(site.structure).map((siteSpace) => siteSpace.space)
            ),
        [sites]
    );
    const revisions = useSpaceRevisions(spacesWithCustomHomePage);
    const items = React.useMemo(
        () => sites.map((site) => getSiteTreeItem(site, { revisions })),
        [revisions, sites]
    );

    const itemsToRender = React.useMemo<AnySiteItem[]>(() => {
        const siteItem = items.find((item) => item.id === siteId);
        if (!siteItem) {
            return items;
        }

        return siteItem.children.map((item) =>
            mapSiteTreeItemToSidebarItem(item, {
                organizationId,
                siteId,
                revisions,
            })
        );
    }, [items, organizationId, siteId, revisions]);

    const { filteredItems, isSearching, normalizedSearch } = useSiteTreeSearch({
        items: itemsToRender,
        searchQuery,
    });

    const sidebarItems = React.useMemo(
        () => filteredItems.map((item) => addEmptyStateItemsToSiteTree(item)),
        [filteredItems]
    );

    const defaultExpandedSiteKeys = React.useMemo<Set<Key>>(
        () =>
            new Set(
                sidebarItems
                    .filter((item) => 'children' in item && item.children.length > 0)
                    .map((item) => item.id)
            ),
        [sidebarItems]
    );
    const expandableSiteKeys = React.useMemo<Set<Key>>(
        () => new Set(getExpandableSiteItemKeys(sidebarItems)),
        [sidebarItems]
    );
    const searchExpandableSiteKeys = React.useMemo<Set<Key>>(
        () => expandableSiteKeys,
        [expandableSiteKeys]
    );

    const emptyStateKeys = React.useMemo(
        () => sidebarItems.flatMap((item) => getEmptyStateKeys(item)),
        [sidebarItems]
    );

    // Keep site's expandable items in sync with rendered items
    // and only update when set content changed.
    const setExpandableSiteItems = useSetAtom(expandableSiteItemsAtom);
    React.useEffect(() => {
        setExpandableSiteItems((prev) => {
            if (prev.size === expandableSiteKeys.size) {
                let isSameSet = true;
                for (const key of expandableSiteKeys) {
                    if (!prev.has(key)) {
                        isSameSet = false;
                        break;
                    }
                }

                if (isSameSet) {
                    return prev;
                }
            }

            return expandableSiteKeys;
        });
    }, [expandableSiteKeys, setExpandableSiteItems]);

    const expandedState = useSiteItemsExpandedState({
        expandableKeys: expandableSiteKeys,
        defaultExpandedKeys: defaultExpandedSiteKeys,
    });

    const activeSpaceId = screen.type === 'space' && !isSearching ? screen.space : undefined;
    const activeItemPath = React.useMemo(
        () => (activeSpaceId ? getSiteSpaceItemPath(sidebarItems, activeSpaceId) : null),
        [activeSpaceId, sidebarItems]
    );
    const setExpandedSiteItems = useSetAtom(expandedSiteItemsAtom);
    React.useLayoutEffect(() => {
        const ancestorKeys = activeItemPath?.slice(0, -1);
        if (!ancestorKeys?.length) {
            return;
        }

        setExpandedSiteItems((prev) => {
            const next = new Set(prev);
            for (const key of ancestorKeys) {
                next.add(key);
            }
            return next.size === prev.size ? prev : next;
        });
    }, [activeItemPath, setExpandedSiteItems]);

    const activeItemId = activeItemPath?.at(-1);
    const activeItemRef = React.useCallback((element: HTMLSpanElement | null) => {
        element?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, []);

    if (normalizedSearch && filteredItems.length === 0) {
        return (
            <SidebarEmptyState>{`No results found for "${normalizedSearch}"`}</SidebarEmptyState>
        );
    }

    if (sidebarItems.length === 0) {
        return <SidebarEmptyState>No content created yet</SidebarEmptyState>;
    }

    return (
        <SidebarTree<SidebarSiteTreeItem>
            // Temporary workaround for a bug when switching between orgs & sites.
            // When switching sites: we need to remount the site tree to reset its internal state.
            key={siteId || organizationId}
            dependencies={[
                screen.type,
                screen.type === 'site'
                    ? screen.site
                    : screen.type === 'space'
                      ? screen.space
                      : null,
                isSearching,
            ]}
            items={sidebarItems}
            aria-label="Docs sites"
            disabledKeys={emptyStateKeys}
            {...(isSearching
                ? {
                      expandedKeys: searchExpandableSiteKeys,
                      onExpandedChange: () => {},
                  }
                : expandedState)}
        >
            {function renderItem(treeItem) {
                if (treeItem.type === 'site-section-group-empty-state') {
                    return (
                        <HeadlessTreeItem
                            textValue={treeItem.title}
                            className="pl-[calc((var(--tree-item-level)-1)*0.75rem+var(--px))] [--px:0.375rem]"
                        >
                            <HeadlessTreeItemContent>
                                <SidebarEmptyState className="py-1.5">
                                    {treeItem.title}
                                </SidebarEmptyState>
                            </HeadlessTreeItemContent>
                        </HeadlessTreeItem>
                    );
                }

                const isExpandable = 'children' in treeItem && treeItem.children.length > 0;
                const href = 'href' in treeItem ? treeItem.href : undefined;
                const isActive = 'isActive' in treeItem ? treeItem.isActive : undefined;
                const active = isActive?.(screen);

                return (
                    <SidebarTreeItem href={href} isActive={active}>
                        <SiteSidebarTreeItemContent
                            value={treeItem}
                            isExpandable={isExpandable}
                            hideExpandButton={isSearching}
                            siteActionsContext={siteActionsContext}
                            textRef={
                                active && treeItem.id === activeItemId ? activeItemRef : undefined
                            }
                        />
                        {'children' in treeItem ? (
                            <Collection items={treeItem.children}>{renderItem}</Collection>
                        ) : null}
                    </SidebarTreeItem>
                );
            }}
        </SidebarTree>
    );
}

function useSiteTreeSearch(props: { items: AnySiteItem[]; searchQuery?: string }): {
    filteredItems: AnySiteItem[];
    isSearching: boolean;
    normalizedSearch: string;
} {
    const { items, searchQuery } = props;
    const normalizedSearch = searchQuery?.trim() ?? '';
    const isSearching = normalizedSearch.length > 0;

    const filteredItems = React.useMemo(() => {
        if (!isSearching) {
            return items;
        }

        return searchSiteTreeItems(items, normalizedSearch);
    }, [isSearching, items, normalizedSearch]);

    return { filteredItems, isSearching, normalizedSearch };
}

const SiteSidebarTreeItemContent = React.memo(function SiteSidebarTreeItemContent(props: {
    value: AnySiteItem;
    isExpandable: boolean;
    hideExpandButton: boolean;
    textRef?: React.Ref<HTMLSpanElement>;
    siteActionsContext?: {
        organization: api.Organization;
        site: api.Site;
        sitePermissions: SiteCapability;
        siteStructure: api.SiteStructure;
        upgrade: UseUpgradeSite | null;
    };
}) {
    const { value, isExpandable, hideExpandButton, textRef, siteActionsContext } = props;
    const canShowCreateItemActions = Boolean(siteActionsContext?.sitePermissions.canCreateSite);
    const canShowSiteSectionActions =
        Boolean(siteActionsContext) &&
        (canShowCreateItemActions ||
            (value.type === 'site-section' && Boolean(value.siteSection.urls.published)));
    const canShowSiteSpaceActions =
        Boolean(siteActionsContext) &&
        (canShowCreateItemActions ||
            (value.type === 'site-space' && Boolean(value.siteSpace.urls.published)));
    const hasDraftBadge = isDraftSiteTreeItem(value);
    const actions =
        value.type === 'site-section-group' && siteActionsContext && canShowCreateItemActions ? (
            <SiteSectionGroupActionsButton
                organization={siteActionsContext.organization}
                site={siteActionsContext.site}
                sitePermissions={siteActionsContext.sitePermissions}
                siteSectionGroup={value.siteSectionGroup}
                siteStructure={siteActionsContext.siteStructure}
                kind="sidebar"
                size="small"
                upgrade={siteActionsContext.upgrade}
            />
        ) : value.type === 'site-section' && siteActionsContext && canShowSiteSectionActions ? (
            <SiteSectionActionsButton
                organization={siteActionsContext.organization}
                site={siteActionsContext.site}
                sitePermissions={siteActionsContext.sitePermissions}
                siteSection={value.siteSection}
                siteStructure={siteActionsContext.siteStructure}
                kind="sidebar"
                size="small"
                upgrade={siteActionsContext.upgrade}
            />
        ) : value.type === 'site-space' && siteActionsContext && canShowSiteSpaceActions ? (
            <SiteVariantActionsButton
                organization={siteActionsContext.organization}
                site={siteActionsContext.site}
                sitePermissions={siteActionsContext.sitePermissions}
                siteSpace={value.siteSpace}
                siteStructure={siteActionsContext.siteStructure}
                kind="sidebar"
                size="small"
            />
        ) : null;
    const shouldOverlayDraftBadge = hasDraftBadge && Boolean(actions);

    return (
        <HeadlessTreeItemContent>
            {isExpandable && !hideExpandButton ? (
                <SidebarTreeItemExpandButton icon={value.icon} />
            ) : (
                <SidebarItemIcon icon={value.icon} />
            )}
            <SidebarTreeItemText ref={textRef}>{value.title}</SidebarTreeItemText>
            {actions ? (
                <div className="group/sidebar-actions relative ml-auto flex shrink-0 items-center justify-end overflow-visible">
                    {actions}
                    {shouldOverlayDraftBadge ? (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-end transition-opacity group-hover/sidebar-item:opacity-0 group-has-[[aria-expanded=true]]/sidebar-actions:opacity-0 group-data-[focus-visible]/sidebar-item:opacity-0">
                            <DraftBadge />
                        </div>
                    ) : null}
                </div>
            ) : hasDraftBadge ? (
                <DraftBadge className="mr-0.5 ml-auto" />
            ) : null}
        </HeadlessTreeItemContent>
    );
});

/**
 * Render the structure of the current site in the content sidebar
 *
 */
export function SidebarSiteTree(props: {
    siteContent: api.OrganizationAllSite;
    organization: api.Organization;
    organizationId: string;
    screen: NavigationScreenState;
    sitePermissions: SiteCapability;
    upgrade: UseUpgradeSite | null;
    searchQuery?: string;
}) {
    const {
        siteContent,
        organization,
        organizationId,
        screen,
        sitePermissions,
        upgrade,
        searchQuery,
    } = props;

    const siteActionsContext = React.useMemo(
        () => ({
            organization,
            site: siteContent.site,
            sitePermissions,
            siteStructure: siteContent.structure,
            upgrade,
        }),
        [organization, siteContent.site, siteContent.structure, sitePermissions, upgrade]
    );

    if (!siteContent) {
        return (
            <SidebarSection className="w-full">
                <SidebarEmptyState>Site not found.</SidebarEmptyState>
            </SidebarSection>
        );
    }

    return (
        <SitesTree
            sites={[siteContent]}
            siteId={siteContent.site.id}
            screen={screen}
            organizationId={organizationId}
            siteActionsContext={siteActionsContext}
            searchQuery={searchQuery}
        />
    );
}

/**
 * Navigation target for a site-space in the current site.
 */
function getSiteSpaceHref(props: {
    organizationId: string | undefined;
    siteId: string;
    siteSpace: api.SiteSpace;
    revisions: ReadonlyMap<string, api.Revision> | undefined;
}) {
    const { organizationId, siteId, siteSpace, revisions } = props;
    const pageTarget = siteSpace.pageId
        ? resolvePageTarget(revisions?.get(siteSpace.space.id), siteSpace.pageId)
        : null;

    return {
        screen: {
            type: 'space',
            organization: organizationId,
            site: siteId,
            space: siteSpace.space.id,
            page: pageTarget?.path ?? '',
        },
    };
}

/**
 * Navigation targets for a section with a single site-space in the current site.
 */
function getSiteSectionHref(props: {
    organizationId: string | undefined;
    siteId: string;
    siteSection: api.SiteSection;
    revisions: ReadonlyMap<string, api.Revision> | undefined;
}) {
    const { siteSection, ...context } = props;
    const singleSiteSpace = siteSection.siteSpaces.length === 1 ? siteSection.siteSpaces[0] : null;

    if (!singleSiteSpace) {
        return undefined;
    }

    return getSiteSpaceHref({
        ...context,
        siteSpace: singleSiteSpace,
    });
}

/**
 * Map site tree items to sidebar items with site-scoped navigation hrefs.
 */
function mapSiteTreeItemToSidebarItem(
    item: SiteStructureItem,
    context: {
        organizationId: string | undefined;
        siteId: string;
        revisions: ReadonlyMap<string, api.Revision> | undefined;
    }
): SiteStructureItem {
    switch (item.type) {
        case 'site-space': {
            return {
                ...item,
                href: getSiteSpaceHref({
                    organizationId: context.organizationId,
                    siteId: context.siteId,
                    siteSpace: item.siteSpace,
                    revisions: context.revisions,
                }),
            } as SiteSpaceItem;
        }
        case 'site-section': {
            return {
                ...item,
                href: getSiteSectionHref({
                    organizationId: context.organizationId,
                    siteId: context.siteId,
                    siteSection: item.siteSection,
                    revisions: context.revisions,
                }),
                children: item.children.map((child) =>
                    mapSiteTreeItemToSidebarItem(child, context)
                ),
            } as SiteSectionItem;
        }
        case 'site-section-group': {
            return {
                ...item,
                children: item.children.map((child) =>
                    mapSiteTreeItemToSidebarItem(child, context)
                ),
            } as SiteSectionGroupItem;
        }
    }
}

function addEmptyStateItemsToSiteTree(item: AnySiteItem): SidebarSiteTreeItem {
    if (!('children' in item)) {
        return item;
    }

    if (item.type === 'site-section-group') {
        const children: SidebarSiteTreeItem[] =
            item.children.length > 0
                ? item.children.map((child) => addEmptyStateItemsToSiteTree(child))
                : [createSiteSectionGroupEmptyStateItem(item.id)];

        return {
            ...item,
            children,
        } as SidebarSiteTreeItem;
    }

    const children: SidebarSiteTreeItem[] = item.children.map((child) =>
        addEmptyStateItemsToSiteTree(child)
    );

    return {
        ...item,
        children,
    } as SidebarSiteTreeItem;
}

function createSiteSectionGroupEmptyStateItem(
    siteSectionGroupId: string
): SiteSectionGroupEmptyStateItem {
    return {
        id: `${siteSectionGroupId}:empty-state`,
        type: 'site-section-group-empty-state',
        title: 'No content in this group yet.',
        icon: Icon.InfoCircle,
    };
}

function getEmptyStateKeys(item: SidebarSiteTreeItem): Key[] {
    if (item.type === 'site-section-group-empty-state') {
        return [item.id];
    }

    if (!('children' in item)) {
        return [];
    }

    return item.children.flatMap((child) => getEmptyStateKeys(child));
}

/**
 * Collect the IDs of all currently rendered site items that have children and can be expanded.
 */
function getExpandableSiteItemKeys(items: SidebarSiteTreeItem[]): string[] {
    return items.flatMap((item) => {
        if (!('children' in item) || item.children.length === 0) {
            return [];
        }

        return [item.id, ...getExpandableSiteItemKeys(item.children)];
    });
}

/** Find the IDs leading to a space in the rendered site tree. */
function getSiteSpaceItemPath(items: SidebarSiteTreeItem[], spaceId: string): string[] | null {
    for (const item of items) {
        if (
            (item.type === 'site-space' && item.space.id === spaceId) ||
            (item.type === 'site-section' && item.space?.id === spaceId)
        ) {
            return [item.id];
        }

        if ('children' in item) {
            const childPath = getSiteSpaceItemPath(item.children, spaceId);
            if (childPath) {
                return [item.id, ...childPath];
            }
        }
    }

    return null;
}

function areSetsEqual<T>(left: Set<T>, right: Set<T>): boolean {
    return left.size === right.size && Array.from(left).every((key) => right.has(key));
}
